import { useState, useMemo } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import './PasswordInput.css';

const requirementHints = {
  length: "Add more characters",
  uppercase: "Add an uppercase letter",
  lowercase: "Add a lowercase letter",
  number: "Add a number",
  special: "Add a special character",
};

const PasswordInput = ({ 
  id, 
  name,
  value, 
  onChange, 
  placeholder, 
  showStrength = false, 
  autoComplete,
  required,
  className = ''
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const passwordValue = typeof value === "string" ? value : "";

  const unmetRequirements = useMemo(() => {
    const unmet = [];
    if (passwordValue.length < 8) unmet.push("length");
    if (!/[A-Z]/.test(passwordValue)) unmet.push("uppercase");
    if (!/[a-z]/.test(passwordValue)) unmet.push("lowercase");
    if (!/[0-9]/.test(passwordValue)) unmet.push("number");
    if (!/[^A-Za-z0-9]/.test(passwordValue)) unmet.push("special");
    return unmet;
  }, [passwordValue]);

  const strength = useMemo(() => {
    const metCount = 5 - unmetRequirements.length;
    
    if (metCount === 0) return null;
    if (metCount <= 2) return { label: "Weak", color: "bg-destructive", width: "33%" };
    if (metCount < 5) return { label: "Fair", color: "bg-warning", width: "66%" };
    return { label: "Strong", color: "bg-success", width: "100%" };
  }, [unmetRequirements]);

  const showFeedback = showStrength && passwordValue.length > 0;

  return (
    <div className="password-input-container">
      <div className="password-input-wrapper">
        <input
          type={showPassword ? "text" : "password"}
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className={`input-field ${className}`}
          required={required}
        />
        <button
          type="button"
          className="password-toggle-btn"
          onClick={() => setShowPassword(!showPassword)}
          tabIndex="-1"
          title={showPassword ? "Hide password" : "Show password"}
        >
          {showPassword ? <EyeOff /> : <Eye />}
        </button>
      </div>
      
      {showFeedback && strength && (
        <div className="password-strength-container">
          <div className="password-strength-bar-wrapper">
            <div className="password-strength-bar-bg">
              <div
                className={`password-strength-bar ${strength.color}`}
                style={{ width: strength.width }}
              />
            </div>
            <span className="password-strength-label">
              {strength.label === "Strong" ? "Strong ✓" : strength.label}
            </span>
          </div>
          {strength.label !== "Strong" && unmetRequirements.length > 0 && (
            <p className="password-strength-hint">
              {strength.label === "Fair" 
                ? "Add more criteria for a stronger password"
                : unmetRequirements.slice(0, 2).map(req => requirementHints[req]).join(" · ")}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default PasswordInput;
